import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function SignatureDialog({ open, onClose, title, onSubmit }) {
  const [mode, setMode] = useState("typed");
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const drew = useRef(false);

  useEffect(() => { if (open) { setMode("typed"); setName(""); drew.current = false; } }, [open]);

  const pos = (e, c) => {
    const r = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; drew.current = true; const c = canvasRef.current; const ctx = c.getContext("2d"); const p = pos(e, c); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = canvasRef.current; const ctx = c.getContext("2d"); const p = pos(e, c); ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.strokeStyle = "#0b1a3a"; ctx.lineTo(p.x, p.y); ctx.stroke(); };
  const end = () => { drawing.current = false; };
  const clearCanvas = () => { const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); drew.current = false; };

  const submit = async () => {
    if (!name.trim()) return toast.error("Enter your full name");
    let data = null;
    if (mode === "drawn") {
      if (!drew.current) return toast.error("Please draw your signature first");
      data = canvasRef.current.toDataURL("image/png");
    }
    setSaving(true);
    try { await onSubmit(name.trim(), mode, data); }
    catch (e) { /* handled by caller */ }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card/95 backdrop-blur-xl border-border" data-testid="sign-dialog">
        <DialogHeader><DialogTitle className="font-display">Sign: {title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Full Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. John Doe" data-testid="sign-name" />
          </div>
          <div className="flex gap-2 p-1 rounded-lg bg-secondary w-fit">
            <button onClick={() => setMode("typed")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${mode === "typed" ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid="sign-mode-typed">Type</button>
            <button onClick={() => setMode("drawn")} className={`px-3 py-1.5 rounded-md text-xs font-medium ${mode === "drawn" ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid="sign-mode-drawn">Draw</button>
          </div>
          {mode === "typed" ? (
            <div className="h-24 rounded-lg border border-border bg-white flex items-center justify-center" data-testid="sign-typed-preview">
              <span style={{ fontFamily: "'Brush Script MT','Segoe Script',cursive", fontSize: 34, color: "#0b1a3a" }}>{name || "Your signature"}</span>
            </div>
          ) : (
            <div className="space-y-2">
              <canvas ref={canvasRef} width={420} height={110}
                className="w-full rounded-lg border border-border bg-white touch-none"
                onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
                onTouchStart={start} onTouchMove={move} onTouchEnd={end}
                data-testid="sign-canvas" />
              <Button size="sm" variant="ghost" onClick={clearCanvas} data-testid="sign-clear">Clear</Button>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-lg" data-testid="sign-submit">{saving ? "Signing…" : "Apply Signature"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
